import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Tabs, { TABS_SIZES, TABS_THEMES } from '@wg/wows-react-uikit/components/Tabs/Tabs';

import { root } from '~/preloaded';
import settings from '~/settings';
import { CLAN_BATTLES_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';

import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentAccountClanIdSelector } from '~/store/selectors/currentAccountSelector';
import { checkClanBattlesThunk } from '~/store/thunks/clanBattlesThunks';
import { getWinners } from '~/store/thunks/hallOfFameThunks';
import { isLoggedIn } from '~/utils/auth';

import { AnimatedFetchingComponent } from '~/UIKit';

import styles from './ClanBattles.module.scss';

import type { Tab } from '@wg/wows-react-uikit/components/Tabs/Tabs';
import type { RootState } from '~/store';

const getCurrentTab = (pathname: string) => {
  if (pathname.startsWith(`${root}${ROUTES_MAP.CLAN_BATTLES_HISTORY}`)) {
    return CLAN_BATTLES_TABS.HISTORY;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CLAN_BATTLES_RULES}`)) {
    return CLAN_BATTLES_TABS.RULES;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CLAN_BATTLES_HALL}`)) {
    return CLAN_BATTLES_TABS.HALL;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CLAN_BATTLES_RATING}`)) {
    return CLAN_BATTLES_TABS.RATING;
  }

  return CLAN_BATTLES_TABS.HISTORY;
};

const stateSelector = (state: RootState) => {
  return {
    isFetching: state.ReducerClanBattles.isFetching,
    winners: state.ReducerHallFame?.winnersList,
  };
};

const ClanBattles: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isFetching, winners } = useAppSelector(stateSelector, shallowEqual);
  const clanId = useAppSelector(currentAccountClanIdSelector);

  useMount(() => {
    dwhExport.push(DWH_EVENTS.CLAN_BATTLES.OPEN_PAGE);

    if (isLoggedIn()) {
      void dispatch(checkClanBattlesThunk());
    }
    void dispatch(getWinners());
  });

  const [currentTab, setCurrentTab] = useState(getCurrentTab(location.pathname));
  useEffect(() => {
    setCurrentTab(getCurrentTab(location.pathname));
  }, [location.pathname]);

  const isHallOfFameAvailable = useMemo(() => {
    return settings.ladder.hallOfFameIsEnabled && !isEmpty(winners);
  }, [winners]);

  const tabs: Tab<CLAN_BATTLES_TABS>[] = useMemo(() => {
    const tabs = [
      { value: CLAN_BATTLES_TABS.HISTORY, content: t('Клановые бои') },
      { value: CLAN_BATTLES_TABS.RATING, content: t('Рейтинг кланов') },
    ];

    if (isHallOfFameAvailable) {
      tabs.push({ value: CLAN_BATTLES_TABS.HALL, content: t('Зал славы') });
    }

    if (clanId) {
      tabs.push({ value: CLAN_BATTLES_TABS.RULES, content: t('Правила') });
    }

    return tabs;
  }, [clanId, isHallOfFameAvailable]);

  const onToggleTab = useCallback(
    (value: CLAN_BATTLES_TABS) => {
      navigate(`${root}${ROUTES_MAP.CLAN_BATTLES}/${value}`, { replace: true });
    },
    [navigate],
  );

  return (
    <AnimatedFetchingComponent className={styles.wrapper} isFetching={isFetching}>
      <div className={classNames(styles.tabsNavigation)}>
        <Tabs
          classNameWrapper={styles.tabs}
          theme={TABS_THEMES.CLIENT}
          size={TABS_SIZES.M}
          isUppercase
          smallHeight
          tabSelected={currentTab}
          tabs={tabs}
          onToggleTab={onToggleTab}
        />
      </div>
      <Outlet />
    </AnimatedFetchingComponent>
  );
};

export default memo(ClanBattles);
